/**
 * EmployeeDokumenteTab.jsx
 * Dokumente-Tab in EmployeeDetail.
 *
 * NEU: Client-seitige Bildkomprimierung (≤2 MB) + Direkt-Scan via Kamera + Crop/Rotate UI.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Folder, FolderOpen, FileText, Upload, Trash2, Eye, Download,
  ChevronRight, ChevronDown, CheckCircle2, Clock, Loader2, PenLine,
  Camera, RotateCw, ZoomIn, ZoomOut, Check, X,
} from 'lucide-react';
import { PdfViewer } from './PdfViewer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const MAX_BYTES = 2 * 1024 * 1024; // 2 MB

// ── Bild-Komprimierung ────────────────────────────────────────────────────────
async function compressImageBlob(blob, maxBytes = MAX_BYTES) {
  // Timeout: 15s – verhindert ewiges Hängen bei HEIC/kaputten Dateien
  return Promise.race([
    new Promise((_, rej) => setTimeout(() => rej(new Error('Komprimierung Timeout')), 15000)),
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        try {
          let { naturalWidth: w, naturalHeight: h } = img;
          if (!w || !h) { reject(new Error('Bild hat ungültige Dimensionen')); return; }
          const maxDim = 1920; // iOS-safe: verhindert OOM bei 12MP+ Fotos
          if (w > maxDim || h > maxDim) {
            const r = Math.min(maxDim / w, maxDim / h);
            w = Math.floor(w * r);
            h = Math.floor(h * r);
          }
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          let quality = 0.88;
          const tryBlob = () => {
            canvas.toBlob(result => {
              if (!result) { reject(new Error('Canvas toBlob fehlgeschlagen')); return; }
              if (result.size <= maxBytes || quality <= 0.1) {
                resolve(result);
              } else {
                quality = Math.max(0.1, quality - 0.08);
                tryBlob();
              }
            }, 'image/jpeg', quality);
          };
          tryBlob();
        } catch (e) { reject(e); }
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Bild-Ladevorgang fehlgeschlagen')); };
      img.src = url;
    })
  ]);
}

// ── Crop-Bereich aus Canvas ausschneiden ─────────────────────────────────────
// Korrekte Implementierung per react-easy-crop Dokumentation:
// 1. Vollbild mit Rotation auf Zwischen-Canvas zeichnen
// 2. Crop-Bereich vom Zwischen-Canvas extrahieren
async function getCroppedBlob(imageSrc, croppedAreaPixels, rotation = 0) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const rad = (rotation * Math.PI) / 180;
        const { naturalWidth: iw, naturalHeight: ih } = img;

        // Schritt 1: Vollbild rotiert auf Zwischen-Canvas
        const rotCanvas = document.createElement('canvas');
        rotCanvas.width = iw;
        rotCanvas.height = ih;
        const rotCtx = rotCanvas.getContext('2d');
        rotCtx.translate(iw / 2, ih / 2);
        rotCtx.rotate(rad);
        rotCtx.translate(-iw / 2, -ih / 2);
        rotCtx.drawImage(img, 0, 0);

        // Schritt 2: Crop aus rotiertem Canvas
        const { x, y, width, height } = croppedAreaPixels;
        const cropCanvas = document.createElement('canvas');
        cropCanvas.width = width;
        cropCanvas.height = height;
        const cropCtx = cropCanvas.getContext('2d');
        cropCtx.drawImage(rotCanvas, x, y, width, height, 0, 0, width, height);

        cropCanvas.toBlob(
          b => b ? resolve(b) : reject(new Error('Canvas toBlob lieferte null')),
          'image/jpeg',
          0.9
        );
      } catch (e) { reject(e); }
    };
    img.onerror = () => reject(new Error('Bild konnte nicht geladen werden'));
    img.src = imageSrc;
  });
}

function isImageFile(file) {
  return file.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
}

// ── Ordner-Baum Komponenten (unverändert) ─────────────────────────────────────
function buildTree(folders) {
  const map = {};
  const roots = [];
  folders.forEach(f => { map[f.id] = { ...f, children: [] }; });
  folders.forEach(f => {
    if (f.parent_folder_id && map[f.parent_folder_id]) {
      map[f.parent_folder_id].children.push(map[f.id]);
    } else {
      roots.push(map[f.id]);
    }
  });
  return roots;
}

function DocRow({ doc, onView, onDelete, isAdmin }) {
  const confirmed = !!doc.confirmed_at;
  return (
    <div className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-muted/30 group">
      <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate">{doc.file_name}</p>
        <p className="text-[10px] text-muted-foreground">
          {new Date(doc.uploaded_at).toLocaleDateString('de-DE')} · {doc.uploaded_by_name}
        </p>
      </div>
      {doc.require_signature && (
        confirmed
          ? <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-green-700 border-green-300 shrink-0">
              <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />Unterschrieben
            </Badge>
          : <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-amber-700 border-amber-300 shrink-0">
              <Clock className="h-2.5 w-2.5 mr-0.5" />Ausstehend
            </Badge>
      )}
      <div className="flex items-center gap-1 shrink-0">
        <button type="button" onClick={() => onView(doc)} title="Ansehen"
          className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-primary transition-colors">
          <Eye className="h-3.5 w-3.5" />
        </button>
        {isAdmin && (
          <button type="button" onClick={() => onDelete(doc)} title="Löschen"
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-destructive transition-colors">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function FolderSection({ node, docs, depth = 0, onUpload, onView, onDelete, isAdmin, isSL }) {
  const [open, setOpen] = useState(true);
  const folderDocs = docs.filter(d => d.folder_id === node.id);
  const pendingSig = folderDocs.filter(d => d.require_signature && !d.confirmed_at).length;

  return (
    <div className={depth > 0 ? 'ml-4 border-l border-border/40 pl-3' : ''}>
      <div className="flex items-center gap-2 py-1.5 px-1 rounded-lg hover:bg-muted/20 group cursor-pointer"
        onClick={() => setOpen(v => !v)}>
        <button type="button" className="text-muted-foreground shrink-0">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        {open ? <FolderOpen className="h-4 w-4 text-amber-500 shrink-0" /> : <Folder className="h-4 w-4 text-amber-500 shrink-0" />}
        <span className="flex-1 text-sm font-medium">{node.name}</span>
        {node.require_signature && <PenLine className="h-3 w-3 text-violet-500 shrink-0" />}
        {pendingSig > 0 && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700">{pendingSig} offen</Badge>
        )}
        {folderDocs.length > 0 && pendingSig === 0 && (
          <span className="text-[10px] text-muted-foreground">{folderDocs.length}</span>
        )}
        {(isAdmin || isSL) && (
          <button type="button"
            onClick={e => { e.stopPropagation(); onUpload(node); }}
            className="flex items-center gap-1 px-2 py-1 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary text-xs font-medium shrink-0 transition-colors"
            data-testid={`upload-folder-${node.id}`}>
            <Upload className="h-3 w-3" />
            <span className="hidden sm:inline">Hochladen</span>
          </button>
        )}
      </div>

      {open && (
        <div className="ml-5">
          {folderDocs.length === 0 && !node.children?.length && (
            (isAdmin || isSL) ? (
              <button type="button" onClick={() => onUpload(node)}
                className="mt-1 mb-1 w-full flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl border-2 border-dashed border-border hover:border-primary/50 hover:bg-primary/5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors"
                data-testid={`upload-empty-folder-${node.id}`}>
                <Upload className="h-3.5 w-3.5" />
                Noch keine Datei — Jetzt hochladen
              </button>
            ) : (
              <p className="text-[11px] text-muted-foreground/60 py-1 pl-1 italic">Leer</p>
            )
          )}
          {folderDocs.map(d => (
            <DocRow key={d.id} doc={d} onView={onView} onDelete={onDelete} isAdmin={isAdmin} />
          ))}
          {node.children?.map(child => (
            <FolderSection key={child.id} node={child} docs={docs} depth={depth + 1}
              onUpload={onUpload} onView={onView} onDelete={onDelete}
              isAdmin={isAdmin} isSL={isSL} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Crop Modal ────────────────────────────────────────────────────────────────
function CropModal({ src, fileName, onConfirm, onCancel, processing }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPx, setCroppedAreaPx] = useState(null);

  return (
    <div className="fixed inset-0 bg-black/90 z-[60] flex flex-col" data-testid="crop-modal">
      <div className="flex items-center justify-between px-4 py-3 bg-black/50 text-white shrink-0">
        <span className="text-sm font-medium truncate max-w-[200px]">{fileName}</span>
        <button onClick={onCancel} className="p-1 text-white/60 hover:text-white">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Crop-Bereich */}
      <div className="flex-1 relative bg-black min-h-0">
        <Cropper
          image={src}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={undefined}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={(_, pixels) => setCroppedAreaPx(pixels)}
          style={{ containerStyle: { background: '#000' } }}
        />
      </div>

      {/* Steuerung */}
      <div className="bg-black/80 px-4 py-4 space-y-3 shrink-0">
        {/* Zoom */}
        <div className="flex items-center gap-3">
          <ZoomOut className="h-4 w-4 text-white/60 shrink-0" />
          <input type="range" min={1} max={3} step={0.05}
            value={zoom} onChange={e => setZoom(Number(e.target.value))}
            className="flex-1 accent-white" />
          <ZoomIn className="h-4 w-4 text-white/60 shrink-0" />
        </div>
        {/* Drehen */}
        <div className="flex items-center justify-between gap-2">
          <button onClick={() => setRotation(r => r - 90)}
            className="flex items-center gap-1.5 text-white/80 hover:text-white text-xs bg-white/10 hover:bg-white/20 px-3 py-2 rounded-lg transition-colors">
            <RotateCw className="h-3.5 w-3.5 scale-x-[-1]" /> Links drehen
          </button>
          <span className="text-white/50 text-xs">{((rotation % 360) + 360) % 360}°</span>
          <button onClick={() => setRotation(r => r + 90)}
            className="flex items-center gap-1.5 text-white/80 hover:text-white text-xs bg-white/10 hover:bg-white/20 px-3 py-2 rounded-lg transition-colors">
            <RotateCw className="h-3.5 w-3.5" /> Rechts drehen
          </button>
        </div>
        {/* Bestätigen */}
        <button
          onClick={() => croppedAreaPx && onConfirm(croppedAreaPx, rotation)}
          disabled={processing || !croppedAreaPx}
          data-testid="crop-confirm-btn"
          className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-colors">
          {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          {processing ? 'Wird komprimiert …' : 'Zuschneiden & Hochladen'}
        </button>
      </div>
    </div>
  );
}

// ── Haupt-Komponente ──────────────────────────────────────────────────────────
export function EmployeeDokumenteTab({ employeeId, userRole }) {
  const [data, setData] = useState({ folders: [], documents: [] });
  const [loading, setLoading] = useState(true);
  const [uploadDlg, setUploadDlg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cropDlg, setCropDlg] = useState(null); // { src, fileName, blob }
  const [viewUrl, setViewUrl] = useState(null);
  const [delDlg, setDelDlg] = useState(null);

  const isAdmin = userRole === 'admin';
  const isSL = userRole === 'stationsleitung';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/employee-documents/by-employee/${employeeId}`, { headers: authH() });
      setData(r.data || { folders: [], documents: [] });
    } catch { toast.error('Fehler beim Laden der Dokumente'); }
    finally { setLoading(false); }
  }, [employeeId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Datei hochladen – folderId explizit übergeben (iOS-safe: kein uploadDlg aus Closure) ──
  const uploadBlob = async (blob, fileName, folderId) => {
    const resolvedFolderId = folderId
      || uploadDlg?.folder?.id
      || sessionStorage.getItem('upload_folder_id');
    if (!resolvedFolderId) {
      toast.error('Ordner nicht mehr verfügbar — bitte Seite neu laden und erneut versuchen');
      throw new Error('no folder id');
    }
    try {
      const fd = new FormData();
      fd.append('employee_id', employeeId);
      fd.append('folder_id', resolvedFolderId);
      fd.append('file', blob, fileName);
      await axios.post(`${API}/employee-documents/upload`, fd, {
        headers: { ...authH(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Dokument hochgeladen');
      sessionStorage.removeItem('upload_folder_id');
      setUploadDlg(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload fehlgeschlagen');
      throw e;
    }
  };

  // ── Normale Dateiauswahl ──────────────────────────────────────────────────
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // iOS-safe: Folder-ID aus DOM (data-attr) oder sessionStorage lesen
    const folderId = e.target.dataset?.folderId
      || e.target.closest('[data-folder-id]')?.dataset?.folderId
      || uploadDlg?.folder?.id
      || sessionStorage.getItem('upload_folder_id');
    if (!folderId) {
      toast.error('Ordner nicht verfügbar — bitte Dialog neu öffnen');
      return;
    }
    e.target.value = '';
    setUploading(true);
    try {
      if (isImageFile(file)) {
        let toUpload = file;
        let fileName = file.name;
        try {
          toUpload = await compressImageBlob(file);
          fileName = file.name.replace(/\.[^.]+$/, '') + '_komprimiert.jpg';
        } catch (compErr) {
          console.warn('Komprimierung übersprungen:', compErr.message);
        }
        await uploadBlob(toUpload, fileName, folderId);
      } else {
        await uploadBlob(file, file.name, folderId);
      }
    } catch (err) {
      if (err.message !== 'no folder id') {
        toast.error('Upload fehlgeschlagen — bitte nochmal versuchen');
      }
    } finally {
      setUploading(false);
    }
  };

  // ── Kamera-Capture ────────────────────────────────────────────────────────
  const handleCameraCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const folderId = e.target.dataset?.folderId
      || e.target.closest('[data-folder-id]')?.dataset?.folderId
      || uploadDlg?.folder?.id
      || sessionStorage.getItem('upload_folder_id');
    e.target.value = '';
    const src = URL.createObjectURL(file);
    setCropDlg({ src, fileName: 'foto_scan.jpg', folderId });
  };

  // ── Crop bestätigt → Komprimieren → Hochladen ────────────────────────────
  const handleCropConfirm = async (croppedAreaPixels, rotation) => {
    if (!cropDlg) return;
    setUploading(true);
    const srcToRevoke = cropDlg.src;
    try {
      const cropped = await getCroppedBlob(srcToRevoke, croppedAreaPixels, rotation);
      const compressed = await compressImageBlob(cropped);
      const sizeMB = (compressed.size / 1024 / 1024).toFixed(1);
      const name = `foto_scan_${Date.now()}.jpg`;
      const folderId = cropDlg?.folderId || uploadDlg?.folder?.id || sessionStorage.getItem('upload_folder_id');
      await uploadBlob(compressed, name, folderId);
      toast.success(`Foto komprimiert (${sizeMB} MB) und hochgeladen`);
    } catch (err) {
      console.error('Crop/Compress fehlgeschlagen:', err);
      toast.error('Fehler beim Verarbeiten des Fotos — bitte nochmal versuchen');
    } finally {
      // KRITISCH: CropModal IMMER schließen, auch bei Fehler!
      setUploading(false);
      URL.revokeObjectURL(srcToRevoke);
      setCropDlg(null);
    }
  };

  const handleView = async (doc) => {
    try {
      const r = await axios.get(`${API}/employee-documents/${doc.id}/file`, {
        headers: authH(), responseType: 'blob',
      });
      setViewUrl({ url: URL.createObjectURL(r.data), name: doc.file_name, type: r.data.type });
    } catch { toast.error('Datei konnte nicht geladen werden'); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/employee-documents/${delDlg.id}`, { headers: authH() });
      toast.success('Dokument gelöscht');
      setDelDlg(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Löschen'); }
  };

  const tree = buildTree(data.folders);
  const totalPending = data.documents.filter(d => d.require_signature && !d.confirmed_at).length;

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="employee-dokumente-tab">
      <Card>
        <CardHeader className="pb-2 pt-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            Dokumente
            {totalPending > 0 && (
              <Badge variant="secondary" className="bg-amber-100 text-amber-700 text-xs">
                {totalPending} Unterschrift{totalPending > 1 ? 'en' : ''} ausstehend
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tree.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Folder className="h-10 w-10 mx-auto mb-2 opacity-20" />
              <p className="text-sm">Keine Ordner vorhanden.</p>
              {isAdmin && (
                <p className="text-xs mt-1">
                  Ordner in <a href="/admin/personal?tab=dok-ordner" className="text-primary underline">Personal → Dok.-Ordner</a> anlegen.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-0.5">
              {tree.map(node => (
                <FolderSection key={node.id} node={node} docs={data.documents}
                  onUpload={(folder) => {
                    sessionStorage.setItem('upload_folder_id', folder.id);
                    setUploadDlg({ folder });
                  }}
                  onView={handleView} onDelete={setDelDlg}
                  isAdmin={isAdmin} isSL={isSL} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Upload Dialog ────────────────────────────────────────────────── */}
      {uploadDlg && !cropDlg && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !uploading && setUploadDlg(null)}>
          <div className="bg-card rounded-2xl p-5 w-full max-w-sm space-y-4 shadow-xl"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Upload className="h-4 w-4 text-primary" />
                <h3 className="font-semibold text-sm">Dokument hochladen</h3>
              </div>
              {/* Notfall-X immer sichtbar */}
              <button onClick={() => { setUploadDlg(null); setUploading(false); }}
                className="p-1 rounded-full hover:bg-muted text-muted-foreground" title="Schließen">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Ordner: <strong>{uploadDlg.folder.name}</strong>
              {uploadDlg.folder.require_signature && (
                <span className="ml-2 text-violet-600 font-medium">· Unterschrift erforderlich</span>
              )}
            </p>
            <p className="text-[10px] text-muted-foreground bg-muted/40 rounded-lg px-3 py-2">
              Bilder werden automatisch auf max. 2 MB komprimiert.
            </p>

            {uploading ? (
              <div className="flex flex-col items-center py-6 gap-2 text-muted-foreground">
                <Loader2 className="h-7 w-7 animate-spin text-primary" />
                <span className="text-xs">Wird komprimiert & hochgeladen …</span>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {/* Datei auswählen – label statt .click() → iOS-safe, kein History-Bug */}
                <label data-testid="upload-file-btn"
                  className="flex flex-col items-center gap-2 border-2 border-dashed border-border rounded-xl p-4 hover:bg-muted/20 hover:border-primary/50 transition-colors cursor-pointer">
                  <Upload className="h-6 w-6 text-muted-foreground" />
                  <span className="text-xs font-medium text-foreground">Datei auswählen</span>
                  <span className="text-[10px] text-muted-foreground text-center">PDF, Bild, Word</span>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.docx"
                    data-folder-id={uploadDlg?.folder?.id}
                    onChange={handleFileSelect} className="sr-only" />
                </label>

                {/* Foto aufnehmen – label mit capture=environment → iOS öffnet Kamera direkt */}
                <label data-testid="upload-camera-btn"
                  className="flex flex-col items-center gap-2 border-2 border-dashed border-border rounded-xl p-4 hover:bg-muted/20 hover:border-primary/50 transition-colors cursor-pointer">
                  <Camera className="h-6 w-6 text-muted-foreground" />
                  <span className="text-xs font-medium text-foreground">Foto aufnehmen</span>
                  <span className="text-[10px] text-muted-foreground text-center">Kamera direkt öffnen</span>
                  <input type="file" accept="image/*" capture="environment"
                    data-folder-id={uploadDlg?.folder?.id}
                    onChange={handleCameraCapture} className="sr-only" />
                </label>
              </div>
            )}

            {!uploading && (
              <Button variant="outline" className="w-full" onClick={() => setUploadDlg(null)}>
                Abbrechen
              </Button>
            )}
          </div>
        </div>
      )}

      {/* ── Crop Modal ───────────────────────────────────────────────────── */}
      {cropDlg && (
        <CropModal
          src={cropDlg.src}
          fileName={cropDlg.fileName}
          processing={uploading}
          onConfirm={handleCropConfirm}
          onCancel={() => { URL.revokeObjectURL(cropDlg.src); setCropDlg(null); }}
        />
      )}

      {/* ── View Dialog ──────────────────────────────────────────────────── */}
      {viewUrl && (
        <div className="fixed inset-0 bg-black/80 z-50 flex flex-col"
          onClick={() => { URL.revokeObjectURL(viewUrl.url); setViewUrl(null); }}>
          <div className="flex items-center justify-between px-4 py-3 bg-black/50 text-white"
            onClick={e => e.stopPropagation()}>
            <span className="text-sm font-medium truncate">{viewUrl.name}</span>
            <div className="flex items-center gap-2">
              <a href={viewUrl.url} download={viewUrl.name}
                className="flex items-center gap-1 text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg"
                onClick={e => e.stopPropagation()}>
                <Download className="h-3.5 w-3.5" /> Download
              </a>
              <button className="text-white/60 hover:text-white px-2"
                onClick={() => { URL.revokeObjectURL(viewUrl.url); setViewUrl(null); }}>✕</button>
            </div>
          </div>
          <div className="flex-1 overflow-auto" onClick={e => e.stopPropagation()}>
            {viewUrl.type?.startsWith('image/') ? (
              <img src={viewUrl.url} alt={viewUrl.name} className="max-w-full h-auto mx-auto object-contain p-4" />
            ) : (
              <PdfViewer url={viewUrl.url} />
            )}
          </div>
        </div>
      )}

      {/* ── Delete Confirm ───────────────────────────────────────────────── */}
      {delDlg && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setDelDlg(null)}>
          <div className="bg-card rounded-2xl p-5 w-full max-w-sm space-y-4 shadow-xl"
            onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-sm text-destructive">Dokument löschen</h3>
            <p className="text-sm text-muted-foreground">
              <strong>"{delDlg.file_name}"</strong> dauerhaft löschen?
            </p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setDelDlg(null)}>Abbrechen</Button>
              <Button variant="destructive" className="flex-1" onClick={handleDelete}
                data-testid="confirm-delete-doc-btn">Löschen</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
